import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { CostPanel, DriveSourcePicker, PlanReviewPanel, WorkflowStepper } from "./GuttenbergWorkflowPanels";
import { AssetPreview } from "./GuttenbergAnalysisPanels";
import { FullReportPreview, ReportAttributionToggle, paginateBlockHeights } from "./GuttenbergReportPreview";
import { rotateAnnotationPercent, rotateCropPercent } from "./GuttenbergImageAndDiff";
import { capitalizeCaption, inlineMarkdownFromEditable, markdownTableRow, normalizeTableBlock, parseMarkdownBlocks, serializeMarkdownTable, tableBlockToMarkdown, tableStructureToMarkdown } from "./GuttenbergChartMarkdown";

const FALLBACK_AI_PROVIDERS = [
    { id: "openai", label: "OpenAI", available: true },
    { id: "glm", label: "GLM", available: true },
    { id: "claude", label: "Claude", available: true },
    {
        id: "hybrid",
        label: "Hybride",
        available: true,
        description: "OpenAI pilote l'analyse et les outils; Claude redige les sections finales."
    }
];

const AI_MODE_OPTIONS = [
    { id: "standard", label: "Standard", title: "Modeles complets selon le workflow Guttenberg" },
    { id: "smart", label: "Smart", title: "Rapide pour les taches simples, intelligent pour plan, images et modifications" },
    { id: "fast", label: "Fast", title: "Force les prochains appels IA sur les modeles rapides" },
    {
        id: "agents",
        label: "Agents β",
        title: "Superviseur habituel avec investigations de sources et donnees deleguees a GPT-5.6 Luna"
    }
];

function normalizeAiProviders(info) {
    return Array.isArray(info?.providers) && info.providers.length ? info.providers : FALLBACK_AI_PROVIDERS;
}

function normalizeAiModes(info) {
    const backendModes = Array.isArray(info?.modes) ? info.modes : [];
    return AI_MODE_OPTIONS.map((option) => ({
        ...option,
        ...(backendModes.find((item) => item.id === option.id) || {})
    }));
}

function aiProviderLabel(provider) {
    if (provider === "glm") return "GLM";
    if (provider === "claude") return "Claude";
    if (provider === "hybrid") return "Hybride";
    return "OpenAI";
}

function normalizeAiMode(mode, fastMode = false) {
    const value = String(mode || "").toLowerCase();
    if (["standard", "smart", "fast", "agents"].includes(value))
        return value;
    return fastMode ? "fast" : "standard";
}

function aiModeLabel(mode) {
    return AI_MODE_OPTIONS.find((option) => option.id === mode)?.label || "Standard";
}

function reportAssistantActionInstructions(action) {
    return Array.isArray(action?.instructions)
        ? action.instructions.map((item) => String(item ?? "").trim()).filter(Boolean)
        : [];
}

function reportAssistantActionKey(action) {
    return JSON.stringify({
        thread: action?.assistant_thread_id || "",
        branch: action?.branch_id || "",
        instruction: String(action?.instruction ?? "").trim(),
        instructions: reportAssistantActionInstructions(action),
        changeScope: action?.change_scope === "structure" ? "structure" : "content",
        targetSections: Array.isArray(action?.target_section_ids) ? action.target_section_ids : [],
        attachments: Array.isArray(action?.attachment_ids) ? action.attachment_ids : [],
        bankImages: Array.isArray(action?.bank_image_paths) ? action.bank_image_paths : [],
        sourceFiles: Array.isArray(action?.source_file_ids) ? action.source_file_ids : [],
    });
}

function reportAssistantChangeTargets(action, sectionVersions) {
    const versions = Array.isArray(sectionVersions) ? sectionVersions : [];
    const versionsById = new Map(versions.map((version) => [version.id, version]));
    const versionIds = Array.isArray(action?.version_ids)
        ? action.version_ids.map((value) => String(value || "")).filter(Boolean)
        : [];
    const targets = [];
    versionIds.forEach((versionId) => {
        const version = versionsById.get(versionId);
        if (!version)
            return;
        const blocks = Array.isArray(version.changed_blocks) ? version.changed_blocks : [];
        if (!blocks.length) {
            targets.push({
                versionId,
                blockId: "",
                blockIndex: null,
                changeType: "modified",
                sectionId: version.section_id,
                sectionTitle: version.section_title || version.section_id
            });
            return;
        }
        blocks.forEach((block) => targets.push({
            versionId,
            blockId: block.id,
            blockIndex: Number.isFinite(block.block_index) ? block.block_index : null,
            changeType: block.change_type || "modified",
            sectionId: version.section_id,
            sectionTitle: version.section_title || version.section_id
        }));
    });
    if (!targets.length) {
        const sectionIds = Array.isArray(action?.section_ids) ? action.section_ids : [];
        sectionIds.forEach((sectionId) => targets.push({
            versionId: "",
            blockId: "",
            blockIndex: null,
            changeType: "modified",
            sectionId: String(sectionId || ""),
            sectionTitle: String(sectionId || "")
        }));
    }
    return targets.filter((target, index) => target.sectionId && targets.findIndex((item) => item.versionId === target.versionId && item.blockId === target.blockId && item.sectionId === target.sectionId) === index);
}

function pendingClarificationQuestions(analysis) {
    return (analysis?.clarification_questions ?? [])
        .filter((question) => question.status === "pending")
        .sort((left, right) => Number(Boolean(right.blocking)) - Number(Boolean(left.blocking)));
}

function nextClarificationQuestion(questions, activeQuestionId) {
    const pending = Array.isArray(questions) ? questions : [];
    if (!pending.length)
        return null;
    const currentIndex = pending.findIndex((question) => question.id === activeQuestionId);
    if (currentIndex < 0)
        return pending[0];
    return pending[currentIndex + 1] ?? null;
}

export function visualReviewIsCurrent(analysis) {
    if (analysis?.visual_inventory_status !== "ready")
        return false;
    const corpusMatch = Boolean(
        analysis.visual_inventory_corpus_fingerprint
        && analysis.visual_user_review_corpus_fingerprint === analysis.visual_inventory_corpus_fingerprint
    );
    const sourceMatch = Boolean(
        analysis.visual_inventory_source_fingerprint
        && analysis.visual_user_review_source_fingerprint === analysis.visual_inventory_source_fingerprint
    );
    return corpusMatch || sourceMatch;
}

function compactDuration(totalSeconds) {
    const seconds = Math.max(0, Math.floor(totalSeconds || 0));
    if (seconds < 60)
        return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes} min ${seconds % 60} s`;
    const hours = Math.floor(minutes / 60);
    return `${hours} h ${minutes % 60} min`;
}

function jobActivityDetail(job, now) {
    if (!job || !["queued", "running"].includes(job.status))
        return "";
    const createdAt = Date.parse(job.created_at || "");
    const updatedAt = Date.parse(job.updated_at || job.created_at || "");
    const elapsed = Number.isFinite(createdAt) ? Math.max(0, (now - createdAt) / 1000) : 0;
    const quiet = Number.isFinite(updatedAt) ? Math.max(0, (now - updatedAt) / 1000) : 0;
    const quietDetail = quiet >= 20
        ? `derniere mise a jour du job il y a ${compactDuration(quiet)}`
        : "traitement actif";
    return `En cours depuis ${compactDuration(elapsed)} · ${quietDetail}. Les appels IA longs peuvent rester plusieurs minutes sur une meme etape.`;
}

function hasGeneratedReport(report) {
    return Boolean(report?.manifest?.sections?.some((section) => {
        if (["generated", "reviewed", "edited"].includes(section.status))
            return true;
        const markdown = report?.sections?.[section.id] ?? "";
        if (/Redaction en attente/i.test(markdown))
            return false;
        return markdown.replace(/[#*_`\s]/g, "").length > 180;
    }));
}

function shouldAutoGenerateOutline({ project, analysis, clarificationQuestions = [], report, reportLoaded = true, job, busy = false, analysisRunning = false, outlineRunning = false }) {
    if (!project || !analysis || !reportLoaded)
        return false;
    const activeJob = job && ["queued", "running"].includes(job.status);
    const pendingAnalysisQuestions = clarificationQuestions.filter((question) => question.stage !== "review_resolution");
    const outlineStage = analysis.outline_stage || "none";
    const outlineMissing = !analysis.outline_approved && !["draft", "approved"].includes(outlineStage);
    return Boolean(
        project.report_ready
        && project.intake_status === "ready"
        && analysis.analysis_pipeline_status === "completed"
        && visualReviewIsCurrent(analysis)
        && !analysis.analysis_resume_requested
        && pendingAnalysisQuestions.length === 0
        && outlineMissing
        && !hasGeneratedReport(report)
        && !activeJob
        && !busy
        && !analysisRunning
        && !outlineRunning
    );
}

function mergeDriveFilePage(current, next, append = false) {
    if (!append || !current)
        return next;
    const byId = new Map();
    [...(current.files ?? []), ...(next.files ?? [])].forEach((file) => {
        if (file?.id)
            byId.set(file.id, file);
    });
    return { ...next, files: [...byId.values()] };
}

function nextDriveBreadcrumbs(current, folder) {
    if (!folder?.id)
        return current;
    const existingIndex = current.findIndex((crumb) => crumb.id === folder.id);
    if (existingIndex >= 0)
        return [
            ...current.slice(0, existingIndex),
            { id: folder.id, name: folder.name || current[existingIndex].name || "Dossier" }
        ];
    return [...current, { id: folder.id, name: folder.name || "Dossier" }];
}

async function uploadSourceBatch(projectId, files, uploadSource) {
    const uploaded = [];
    for (const file of files) {
        uploaded.push(await uploadSource(projectId, file));
    }
    return uploaded;
}

function sourcePathLabel(source) {
    if (!source)
        return "";
    return source.drive_path || source.display_path || source.source_path || source.path || source.filename || "";
}

function sourceStatusLabel(source) {
    if (!source)
        return "";
    if (source.origin === "deepflow_drive" && source.parse_status === "drive_reference")
        return "reference";
    if (source.origin === "deepflow_drive" && source.parse_status === "rag_indexed_drive")
        return "reference RAG";
    return source.parse_status || "pending";
}

function activeBranchStorageKey(projectId) {
    return `guttenberg_active_branch_${projectId || "default"}`;
}

function getStoredActiveBranchId(projectId) {
    try {
        return localStorage.getItem(activeBranchStorageKey(projectId)) || "main";
    }
    catch {
        return "main";
    }
}

function storeActiveBranchId(projectId, branchId) {
    try {
        localStorage.setItem(activeBranchStorageKey(projectId), branchId || "main");
    }
    catch {
        // Local storage is optional; the in-memory branch state still works.
    }
}

function emptyCollaborationState() {
    return { participants: [], locks: [] };
}

function getCollaborationSessionId() {
    const key = "guttenberg_collaboration_session_id";
    const existing = localStorage.getItem(key);
    if (existing)
        return existing;
    const created = `gt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, created);
    return created;
}

function getCollaborationDisplayName() {
    const stored = localStorage.getItem("guttenberg_display_name");
    if (stored)
        return stored;
    const token = localStorage.getItem("auth_token");
    const payload = decodeJwtPayload(token);
    const name = payload?.name || payload?.email || "Utilisateur";
    return String(name).trim() || "Utilisateur";
}

function decodeJwtPayload(token) {
    if (!token || !token.includes("."))
        return null;
    try {
        const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(window.atob(base64));
    }
    catch {
        return null;
    }
}

function collaborationColor(sessionId) {
    const palette = ["#1b4f8a", "#2d7a4f", "#9a5b13", "#7c3aed", "#b42318", "#0f766e"];
    const score = String(sessionId || "").split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return palette[score % palette.length];
}

function initials(name) {
    const parts = String(name || "U").trim().split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "U";
}

function locationLabel(participant) {
    const tab = participant.tab === "analysis" ? "Analyse" : participant.tab === "costs" ? "Couts" : "Rapport";
    const section = participant.section_title || participant.section_id;
    const branch = participant.branch_id && participant.branch_id !== "main" ? ` - ${participant.branch_id}` : "";
    if (participant.editing && section)
        return `${tab}${branch} - edition ${section}`;
    if (section)
        return `${tab}${branch} - ${section}`;
    return `${tab}${branch}`;
}

function normalizeBranchId(branchId) {
    return branchId || "main";
}

function eventBranchId(update) {
    return update?.branch_id || update?.job?.result?.branch_id || "";
}

function eventTargetsBranch(update, activeBranchId) {
    const targetBranchId = eventBranchId(update);
    if (!targetBranchId)
        return true;
    return normalizeBranchId(targetBranchId) === normalizeBranchId(activeBranchId);
}

function imageProcessingKey(sectionId, imagePath) {
    const cleanSectionId = String(sectionId || "").trim();
    const cleanImagePath = String(imagePath || "").trim();
    if (!cleanSectionId && !cleanImagePath)
        return "";
    return `${cleanSectionId}::${cleanImagePath || "__section__"}`;
}

function branchScopedLocks(locks, activeBranchId) {
    const currentBranchId = normalizeBranchId(activeBranchId);
    return locks.filter((lock) => {
        if (lock.branch_id)
            return normalizeBranchId(lock.branch_id) === currentBranchId;
        if (lock.kind !== "user-edit")
            return true;
        return normalizeBranchId(lock.branch_id) === currentBranchId;
    });
}

function mergeFeedbackTitle(feedback) {
    if (feedback.status === "conflict")
        return "Merge bloque par conflit";
    if (feedback.status === "noop")
        return "Aucune modification a merger";
    return "Merge applique";
}

function mergeFeedbackDetail(feedback) {
    const source = feedback.sourceName || feedback.sourceBranchId || "branche source";
    if (feedback.status === "conflict") {
        const count = feedback.conflicts?.length ?? 0;
        return `${source}: ${count} section(s) modifiee(s) des deux cotes.`;
    }
    if (feedback.status === "noop")
        return `${source}: la branche active contient deja ces changements.`;
    const count = feedback.appliedSections?.length ?? 0;
    return `${source}: ${count} section(s) appliquee(s).`;
}

function branchDepths(branches) {
    const byId = new Map(branches.map((branch) => [branch.id, branch]));
    const cache = new Map();
    function depth(branch) {
        if (!branch || !branch.parent_id || !byId.has(branch.parent_id))
            return 0;
        if (cache.has(branch.id))
            return cache.get(branch.id);
        const value = 1 + depth(byId.get(branch.parent_id));
        cache.set(branch.id, value);
        return value;
    }
    branches.forEach((branch) => cache.set(branch.id, depth(branch)));
    return cache;
}

function formatEuro(value) {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: value < 1 ? 4 : 2 }).format(value);
}

function formatUsd(value) {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "USD", maximumFractionDigits: value < 1 ? 4 : 2 }).format(value);
}

function formatTokenCount(value) {
    if (value >= 1000000)
        return `${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000)
        return `${(value / 1000).toFixed(1)}k`;
    return String(value);
}

function formatCostDuration(value) {
    const milliseconds = Math.max(0, Number(value) || 0);
    if (!milliseconds)
        return "n/d";
    if (milliseconds < 1000)
        return `${Math.round(milliseconds)} ms`;
    if (milliseconds < 60000)
        return `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)} s`;
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.round((milliseconds % 60000) / 1000);
    return `${minutes} min ${seconds} s`;
}

function costOutcomeLabel(outcome) {
    if (outcome === "failed")
        return "Echec";
    if (outcome === "invalid_output")
        return "Sortie invalide";
    return "Termine";
}

function mergeJobTrace(current, incoming) {
    const items = Array.isArray(incoming) ? incoming : [];
    const merged = [];
    const seen = new Set();
    [...(current ?? []), ...items].forEach((item) => {
        if (!item || typeof item !== "object")
            return;
        const title = String(item.title ?? "").trim();
        const message = String(item.message ?? "").trim();
        if (!title && !message)
            return;
        const normalized = {
            ...item,
            title,
            message,
            status: String(item.status ?? "running").trim() || "running",
            section_id: String(item.section_id ?? "").trim(),
            created_at: item.created_at || new Date().toISOString()
        };
        const key = [
            normalized.title,
            normalized.message,
            normalized.status,
            normalized.section_id
        ].join("|");
        if (seen.has(key))
            return;
        seen.add(key);
        merged.push(normalized);
    });
    return merged.slice(-80);
}

function normalizeOutlinePreview(value) {
    if (!Array.isArray(value))
        return [];
    return value
        .filter((item) => !!item && typeof item === "object")
        .map((item, index) => ({
        id: String(item.id ?? `section_${index + 1}`),
        title: String(item.title ?? "").trim(),
        brief: String(item.brief ?? "").trim(),
        estimated_pages: numericOrNull(item.estimated_pages),
        subsections: Array.isArray(item.subsections) ? item.subsections.map((entry) => String(entry).trim()).filter(Boolean) : []
    }))
        .filter((item) => item.title || item.brief);
}

function extractOutlinePreview(raw) {
    const json = extractLikelyJson(raw);
    if (json) {
        try {
            const parsed = JSON.parse(json);
            const sections = normalizeOutlinePreview(parsed.sections);
            if (sections.length)
                return sections;
        }
        catch {
            // Fall back to partial regex parsing while the JSON stream is incomplete.
        }
    }
    const sections = [];
    const objectPattern = /\{[^{}]*(?:"title"|"brief"|"estimated_pages"|"subsections")[^{}]*\}/g;
    for (const match of raw.matchAll(objectPattern)) {
        const chunk = match[0];
        const title = matchJsonString(chunk, "title");
        const brief = matchJsonString(chunk, "brief");
        if (!title && !brief)
            continue;
        sections.push({
            id: matchJsonString(chunk, "id") || `section_${sections.length + 1}`,
            title,
            brief,
            estimated_pages: numericOrNull(matchJsonValue(chunk, "estimated_pages")),
            subsections: matchJsonArrayStrings(chunk, "subsections")
        });
    }
    return sections;
}

function extractLikelyJson(raw) {
    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start === -1 || end <= start)
        return null;
    return raw.slice(start, end + 1);
}

function matchJsonString(chunk, key) {
    const match = chunk.match(new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`));
    return match ? match[1].replace(/\\"/g, "\"").trim() : "";
}

function matchJsonValue(chunk, key) {
    const match = chunk.match(new RegExp(`"${key}"\\s*:\\s*([^,}\\n]+)`));
    return match ? match[1].trim().replace(/^"|"$/g, "") : null;
}

function matchJsonArrayStrings(chunk, key) {
    const match = chunk.match(new RegExp(`"${key}"\\s*:\\s*\\[([^\\]]*)\\]`));
    if (!match)
        return [];
    return Array.from(match[1].matchAll(/"([^"]+)"/g)).map((item) => item[1].trim()).filter(Boolean);
}

function numericOrNull(value) {
    const number = Number(String(value ?? "").replace(",", "."));
    return Number.isFinite(number) && number > 0 ? Number(number.toFixed(2)) : null;
}

function parseTargetPages(value) {
    const number = Number(value.trim().replace(",", "."));
    return Number.isFinite(number) && number > 0 ? Math.round(number) : null;
}

function formatTargetPages(value) {
    return value ? String(value) : "";
}

function buildRichPlanSections(analysis, manifest, visualAssetById = new Map()) {
    const outline = Array.isArray(analysis?.outline_draft) ? analysis.outline_draft : [];
    const manifestSections = Array.isArray(manifest?.sections) ? manifest.sections : [];
    const sourceSections = outline.length ? outline : manifestSections;
    const sectionSpecs = Array.isArray(analysis?.section_specs) ? analysis.section_specs : [];
    const specsBySection = new Map(sectionSpecs.map((spec) => [spec.section_id || spec.id, spec]));
    const contentBlocksBySection = groupBySection(analysis?.writing_blueprint?.content_blocks ?? []);
    const visualSlotsBySection = groupBySection(analysis?.writing_blueprint?.visual_slots ?? []);
    return sourceSections.map((section, index) => {
        const sectionId = section.id || section.section_id || `section-${index}`;
        const spec = specsBySection.get(sectionId) ?? {};
        const details = section.outline_details ?? {};
        const contentBlocks = contentBlocksBySection.get(sectionId) ?? [];
        const visualSlots = visualSlotsBySection.get(sectionId) ?? [];
        const rawVisualRequestsCandidate = section.visual_requests ?? details.visual_requests ?? spec.visual_requests ?? [];
        const rawVisualRequests = Array.isArray(rawVisualRequestsCandidate) ? rawVisualRequestsCandidate : [];
        const subsections = (section.subsections?.length
            ? section.subsections
            : spec.include_points?.length
                ? spec.include_points
                : contentBlocks.map((block) => block.block_title || block.intent).filter(Boolean));
        const dataQuestions = dedupePlanValues([
            ...(spec.data_questions ?? []),
            ...(details.data_questions ?? []),
            ...contentBlocks.flatMap((block) => block.data_questions ?? []),
        ], 12);
        return {
            ...section,
            id: sectionId,
            title: section.title || spec.title || `Section ${index + 1}`,
            role: spec.role || details.role || section.role || "",
            key_message: spec.key_message || details.key_message || section.key_message || "",
            brief: section.brief || details.brief || spec.key_message || "",
            estimated_pages: section.estimated_pages,
            subsections,
            evidence_ids: section.evidence_ids ?? details.evidence_ids ?? spec.evidence_ids ?? [],
            data_questions: dataQuestions,
            content_blocks: contentBlocks,
            visual_slots: visualSlots,
            visual_requests: rawVisualRequests.map((request) => ({
                ...request,
                preferred_assets: (request.preferred_asset_ids ?? [])
                    .map((assetId) => visualAssetById.get(assetId))
                    .filter(Boolean)
            }))
        };
    });
}

function groupBySection(items) {
    const grouped = new Map();
    for (const item of items ?? []) {
        const sectionId = item.section_id;
        if (!sectionId)
            continue;
        if (!grouped.has(sectionId))
            grouped.set(sectionId, []);
        grouped.get(sectionId).push(item);
    }
    return grouped;
}

function dedupePlanValues(values, limit) {
    const seen = new Set();
    const result = [];
    for (const value of values ?? []) {
        const text = String(value ?? "").trim();
        const key = text.toLowerCase();
        if (!text || seen.has(key))
            continue;
        seen.add(key);
        result.push(text);
        if (result.length >= limit)
            break;
    }
    return result;
}

function emptyAnalysisDraft() {
    return {
        analysis_brief: "",
        report_intent: {
            objective: "",
            expectations: [],
            deliverables: [],
            constraints: [],
            audience: ""
        },
        objective: "",
        context: "",
        problem_statement: "",
        scope: "",
        technical_locks: [],
        open_questions: [],
        blocking_questions: [],
        knowledge_summary: "",
        report_brief: ""
    };
}

function toAnalysisDraft(value) {
    return {
        analysis_brief: value.analysis_brief ?? "",
        report_intent: value.report_intent ?? {
            objective: value.objective ?? "",
            expectations: [],
            deliverables: [],
            constraints: [],
            audience: ""
        },
        objective: value.objective ?? "",
        context: value.context ?? "",
        problem_statement: value.problem_statement ?? "",
        scope: value.scope ?? "",
        technical_locks: value.technical_locks ?? [],
        open_questions: value.open_questions ?? [],
        blocking_questions: value.blocking_questions ?? [],
        knowledge_summary: value.knowledge_summary ?? "",
        report_brief: value.report_brief ?? ""
    };
}

function linesFromText(value) {
    return value.split("\n").map((line) => line.trim()).filter(Boolean);
}

function isOutlineInstruction(message) {
    return /\b(plan|sommaire|section|partie|chapitre)\b/i.test(message);
}

function sourceContextDocumentType(value) {
    if (!value || typeof value !== "object" || !("document_type" in value))
        return "";
    return String(value.document_type ?? "");
}

function visualAssetSourceKey(asset) {
    if (!asset)
        return "";
    if (String(asset.source_file_id ?? "").trim())
        return `id:${String(asset.source_file_id).trim()}`;
    const sourcePath = String(asset.source_path ?? "").trim().toLocaleLowerCase();
    if (sourcePath)
        return `path:${sourcePath}`;
    const filename = String(asset.source_filename ?? "").trim().toLocaleLowerCase();
    return filename ? `name:${filename}` : "";
}

function visualAssetSourceLabel(asset) {
    return String(asset?.source_path || asset?.source_filename || "ce document").trim() || "ce document";
}

function sourceImageExclusionSuggestion(asset, images) {
    const sourceKey = visualAssetSourceKey(asset);
    if (!sourceKey)
        return { sourceKey: "", totalCount: 0, rejectedCount: 0, remainingCount: 0, shouldSuggest: false };
    const relatedImages = (images ?? []).filter((candidate) => visualAssetSourceKey(candidate) === sourceKey);
    const rejectedCount = relatedImages.filter((candidate) => candidate.status === "rejected" || candidate.id === asset.id).length;
    const remainingCount = relatedImages.filter((candidate) => candidate.status !== "rejected" && candidate.id !== asset.id).length;
    return {
        sourceKey,
        totalCount: relatedImages.length,
        rejectedCount,
        remainingCount,
        shouldSuggest: rejectedCount >= 2 && remainingCount > 0
    };
}

function sectionHeadings(markdown, fallback) {
    const headings = parseMarkdownBlocks(markdown)
        .map((block) => (block.type === "h2" && "text" in block ? block.text.trim() : ""))
        .filter(Boolean);
    return headings.length ? headings : fallback.filter(Boolean);
}

function slugify(value) {
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}

export {
    AI_MODE_OPTIONS,
    FALLBACK_AI_PROVIDERS,
    activeBranchStorageKey,
    aiModeLabel,
    aiProviderLabel,
    branchDepths,
    branchScopedLocks,
    buildRichPlanSections,
    collaborationColor,
    compactDuration,
    costOutcomeLabel,
    decodeJwtPayload,
    dedupePlanValues,
    emptyAnalysisDraft,
    emptyCollaborationState,
    eventBranchId,
    eventTargetsBranch,
    extractLikelyJson,
    extractOutlinePreview,
    formatCostDuration,
    formatEuro,
    formatTargetPages,
    formatTokenCount,
    formatUsd,
    getCollaborationDisplayName,
    getCollaborationSessionId,
    getStoredActiveBranchId,
    groupBySection,
    hasGeneratedReport,
    imageProcessingKey,
    initials,
    isOutlineInstruction,
    jobActivityDetail,
    linesFromText,
    locationLabel,
    matchJsonArrayStrings,
    matchJsonString,
    matchJsonValue,
    mergeDriveFilePage,
    mergeFeedbackDetail,
    mergeFeedbackTitle,
    mergeJobTrace,
    nextClarificationQuestion,
    nextDriveBreadcrumbs,
    normalizeAiMode,
    normalizeAiModes,
    normalizeAiProviders,
    normalizeBranchId,
    normalizeOutlinePreview,
    numericOrNull,
    parseTargetPages,
    pendingClarificationQuestions,
    reportAssistantActionInstructions,
    reportAssistantActionKey,
    reportAssistantChangeTargets,
    sectionHeadings,
    slugify,
    sourceContextDocumentType,
    sourceImageExclusionSuggestion,
    sourcePathLabel,
    sourceStatusLabel,
    shouldAutoGenerateOutline,
    storeActiveBranchId,
    toAnalysisDraft,
    uploadSourceBatch,
    visualAssetSourceKey,
    visualAssetSourceLabel
};
